import { z } from "zod";
/**
 * Wire-format DTOs for the admin Staff (Team) management endpoints
 * (`/v1/admin/staff/*`).
 *
 * Field naming follows the admin-controller convention: snake_case keys
 * mirroring database columns (`packages/db/src/schema/identity.ts`
 * staff_members + users). The staffer's email + name live on `users`
 * (FK staff_members.user_id -> users.id), not on `staff_members` directly;
 * the list/detail queries JOIN to surface them on the wire row.
 */
// ---------- Enum (mirrored from packages/db/src/schema/identity.ts) ----------
export const StaffRoleValues = [
    "wmart_admin",
    "wmart_sales",
    "wmart_finance",
    "wmart_delivery",
];
export const StaffRole = z.enum(StaffRoleValues);
/**
 * Human-readable label for a staff role (e.g. "Admin", "Sales"). Used by the
 * admin SPA's Team list/edit so the wire value (`wmart_admin`) never leaks
 * into the UI.
 */
export function staffRoleLabel(role) {
    switch (role) {
        case "wmart_admin":
            return "Admin";
        case "wmart_sales":
            return "Sales";
        case "wmart_finance":
            return "Finance";
        case "wmart_delivery":
            return "Delivery";
    }
}
// ---------- Request bodies ----------
export const ListStaffQuery = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    role: StaffRole.optional(),
    q: z.string().max(200).optional(),
});
/**
 * POST /v1/admin/staff body. Invite-based onboarding: NO password — the new
 * staffer receives a set-password link by email (the same Better Auth
 * reset-password flow `seed-first-admin` uses to bootstrap the first admin).
 * `name` + `department` are optional free-text.
 *
 * zod 4 idiom: `z.email()` (not `z.string().email()`), matching
 * password-reset.controller.ts.
 */
export const CreateStaffBody = z.object({
    email: z.email(),
    name: z.string().trim().min(1).max(200).optional(),
    staff_role: StaffRole,
    department: z.string().trim().min(1).max(100).optional(),
});
/**
 * PATCH /v1/admin/staff/:id body. At least one of `staff_role` or
 * `department` must be present — the empty-object PATCH rejects with 400 so
 * callers can't quietly noop (mirrors UpdateCustomerBody).
 *
 * `department` is nullable so an admin can clear it; `staff_role` cannot be
 * nulled (every staffer has a role).
 */
export const UpdateStaffBody = z
    .object({
    staff_role: StaffRole.optional(),
    department: z.string().trim().min(1).max(100).nullable().optional(),
})
    .refine((v) => v.staff_role !== undefined || v.department !== undefined, {
    message: "at least one of staff_role, department is required",
});
